// See [Barrel File Removal FAQ](https://hello.atlassian.net/wiki/x/KJT2aAE)
/* eslint-disable no-barrel-files/no-barrel-files */
import type {
	Maybe,
	SearchJiraBoardProductType,
	SearchJiraIssueProductType,
	SearchJiraProductType,
	SearchLinkedResult,
	SearchResult,
	SearchResultType,
} from '../../../../common/types';
import { type JiraStatusCategoryColorType } from './hydration/types';

/**
 * Hydrating the project avatar field causes additional latency so it is removed in this GraphQL Query
 * Investigation: https://hello.atlassian.net/wiki/x/iKKu8w
 */
const getSearchResultJiraProject = () => `
... on SearchResultJiraProject {
  projectId
  projectKey
  projectType
  lastModifiedDate
  project @skip(if: $skipJiraHydration) {
    webUrl
  }
	socialSignal(tenantId: $tenantId) @include(if: $includeSocialSignals) {
		signal
		timestamp
		user {
			accountId
			name
			picture
			... on AtlassianAccountUser {
				email
				extendedProfile {
					jobTitle
				}
			}
		}
	}
}`;

export interface SearchResultJiraProject extends SearchResult {
	projectId: string;
	projectKey: string;
	lastModifiedDate?: string;
	projectType: SearchJiraProductType;
	// Hydrated field
	project?: {
		webUrl?: string;
	};
	socialSignal: Maybe<{
		/** Should be either "comment" , "edit". */
		signal: Maybe<string>;
		timestamp: Maybe<string>;
		user: Maybe<{
			accountId: string;
			name: string;
			picture: string;
			email: Maybe<string>;
			extendedProfile: Maybe<{
				jobTitle: Maybe<string>;
			}>;
		}>;
	}>;
	__typename: 'SearchResultJiraProject';
}

export const isSearchResultJiraProject: (
	partial: SearchResult,
) => partial is SearchResultJiraProject = (
	partial: SearchResult,
): partial is SearchResultJiraProject => {
	return partial.__typename === 'SearchResultJiraProject';
};

/**
 * Hydrating issueTypeField and statusField have caused additional latency so it is removed in this GraphQL Query
 * Investigation: https://hello.atlassian.net/wiki/x/iKKu8w
 */
/**
 * Quick Find Jira issue hydration.
 *
 * The `rovo_qf_jira_fast_hydration` experiment is implemented with GraphQL `@include` / `@skip`
 * directives on the `$includeFastJiraHydration` query variable (the same pattern used for
 * `$includeSocialSignals` / `$includeJiraIssueType`) rather than string interpolation — so the
 * variable is set once by the caller and no boolean is prop-drilled through the partial builders.
 *
 * When `$includeFastJiraHydration` is true, the lighter value fields let the gira hydration do less
 * work and the higher-cost fields are excluded via `@skip`:
 *   - `issueType { name avatar { medium } }` replaces `issueTypeAvatarUrl` + `issueTypeField`
 *   - `project { id name projectType }` replaces the `fieldsById(ids: ["project"])` connection
 *   - `assigneeField` is dropped entirely (Quick Find never renders the assignee).
 * When false, the query is byte-identical to the legacy slow-path shape.
 * See go/jira-issue-value-field-deprecated.
 */
// oxlint-disable-next-line eslint/no-redeclare
export const SearchResultJiraIssue = `
... on SearchResultJiraIssue {
  id
  title
  type
  description
  url
  iconUrl
  lastModifiedDate
  issueTypeId
  issueKey @include(if: $skipJiraHydration)
  projectName @include(if: $skipJiraHydration)
  status {
    id
    name
  }
  issue @skip(if: $skipJiraHydration) {
    webUrl
		key
		assigneeField @skip(if: $includeFastJiraHydration) {
			user {
				name
				picture
			}
		}
		fieldsById(ids: ["project"]) @skip(if: $includeFastJiraHydration) {
			edges {
				node {
					__typename
					name
					... on JiraProjectField {
						project {
							id
							name
							projectType
						}
					}
				}
			}
		}
		issueTypeAvatarUrl @skip(if: $includeFastJiraHydration)
		issueTypeField @include(if: $includeJiraIssueType) @skip(if: $includeFastJiraHydration) {
			id
			issueType {
				id
				name
			}
		}
		issueType @include(if: $includeFastJiraHydration) {
			name
			avatar {
				medium
			}
		}
		project @include(if: $includeFastJiraHydration) {
			id
			name
			projectType
		}
  }
  socialSignal(tenantId: $tenantId) @include(if: $includeSocialSignals) {
	    signal
		timestamp
		user {
			accountId
			name
			picture
			... on AtlassianAccountUser {
				email
				extendedProfile {
					jobTitle
				}
			}
	    }
  }
	linkedResults {
		id
		datasourceId @include(if: $includeDatasourceId)
		title
		url
		type
		description
		lastModifiedDate
		category
		subtype
		integrationId
		providerId
	}
  __typename
}
`;

export type JiraProjectField = {
	__typename: 'JiraProjectField';
	name: 'Project';
	project: {
		name: string;
		id: string;
		projectType?: SearchJiraIssueProductType;
	};
};

export type JiraIssueField = JiraProjectField;

export type SearchResultJiraIssue = {
	id: string;
	issueKey?: Maybe<string>;
	projectName?: Maybe<string>;
	title: string;
	url: string;
	iconUrl?: Maybe<string>;
	type: SearchResultType;
	description: string;
	lastModifiedDate?: string;
	status?: {
		name?: string;
		colorName?: JiraStatusCategoryColorType;
	};
	// Hydrated field
	issue?: {
		webUrl?: string;
		key: string;
		// Slow-path fields (rovo_qf_jira_fast_hydration OFF). Optional because the fast path omits
		// them.
		fieldsById?: {
			edges: {
				node: JiraProjectField;
			}[];
		};
		issueTypeAvatarUrl?: string;
		issueTypeField?: {
			id: string;
			issueType?: {
				id: string;
				name: string;
			};
		};
		assigneeField?: {
			user: Maybe<{
				name: string;
				picture: string;
			}>;
		};
		// Fast-path value fields (rovo_qf_jira_fast_hydration ON). Optional because the slow path
		// omits them.
		issueType?: {
			name: string;
			avatar?: Maybe<{
				medium?: Maybe<string>;
			}>;
		};
		project?: {
			id: string;
			name: string;
			projectType?: Maybe<string>;
		};
	};
	socialSignal: Maybe<{
		/** Should be either "comment" , "edit", or "share". */
		signal: Maybe<string>;
		timestamp: Maybe<string>;
		user: Maybe<{
			accountId: string;
			name: string;
			picture: string;
			email: Maybe<string>;
			extendedProfile: Maybe<{
				jobTitle: Maybe<string>;
			}>;
		}>;
	}>;
	linkedResults?: Maybe<Array<SearchLinkedResult>>;
	__typename: 'SearchResultJiraIssue';
};

export const mapJiraIssuePartial: (partial: SearchResultJiraIssue) => {
	url: string;
	id: string;
	title: string;
	iconUrl?: Maybe<string>;
	type: SearchResultType;
	description: string;
	lastModifiedDate?: string;
	status?: {
		name?: string;
		colorName?: JiraStatusCategoryColorType;
	};
	// Reuse the input issue shape so the fast/slow field variants stay in sync automatically.
	issue?: SearchResultJiraIssue['issue'];
	socialSignal: Maybe<{
		/** Should be either "comment" , "edit", or "share". */
		signal: Maybe<string>;
		timestamp: Maybe<string>;
		user: Maybe<{
			accountId: string;
			name: string;
			picture: string;
			email: Maybe<string>;
			extendedProfile: Maybe<{
				jobTitle: Maybe<string>;
			}>;
		}>;
	}>;
	linkedResults?: Maybe<Array<SearchLinkedResult>>;
	__typename: 'SearchResultJiraIssue';
} = (partial: SearchResultJiraIssue) => {
	return {
		...partial,
		url: partial.issue?.webUrl || partial.url,
	};
};

export const mapJiraProjectPartial: (
	partial: SearchResultJiraProject,
) => SearchResultJiraProject = (partial: SearchResultJiraProject): SearchResultJiraProject => ({
	...partial,
	url: partial.project?.webUrl || partial.url,
});

export const isSearchResultJiraIssue: (
	partial: SearchResult,
) => partial is SearchResultJiraIssue = (
	partial: SearchResult,
): partial is SearchResultJiraIssue => {
	return partial.__typename === 'SearchResultJiraIssue';
};

export const isJiraProjectField: (partial: JiraIssueField) => partial is JiraProjectField = (
	partial: JiraIssueField,
): partial is JiraProjectField => {
	return partial.__typename === 'JiraProjectField' && partial.name === 'Project';
};

// oxlint-disable-next-line eslint/no-redeclare
export const SearchResultJiraFilter = ``;

export interface SearchResultJiraFilter extends SearchResult {
	type: SearchResultType;
	__typename: 'SearchResultJiraFilter';
}

export const isSearchResultJiraFilter: (
	partial: SearchResult,
) => partial is SearchResultJiraFilter = (
	partial: SearchResult,
): partial is SearchResultJiraFilter => {
	return partial.__typename === 'SearchResultJiraFilter';
};

// oxlint-disable-next-line eslint/no-redeclare
export const SearchResultJiraDashboard = ``;

export interface SearchResultJiraDashboard extends SearchResult {
	type: SearchResultType;
	__typename: 'SearchResultJiraDashboard';
}

export const isSearchResultJiraDashboard: (
	partial: SearchResult,
) => partial is SearchResultJiraDashboard = (
	partial: SearchResult,
): partial is SearchResultJiraDashboard => {
	return partial.__typename === 'SearchResultJiraDashboard';
};

// oxlint-disable-next-line eslint/no-redeclare
export const SearchResultJiraBoard = `
... on SearchResultJiraBoard {
  boardId
  container @include(if: $includeJiraBoardContainer) {
		__typename
		... on SearchResultJiraBoardProjectContainer {
			__typename
			projectName
		}
		... on SearchResultJiraBoardUserContainer {
			__typename
			userName
		}
	}
	board @skip(if: $skipJiraHydration) {
		boardFrontendUrl
		iconUrl
	}
  product
}
`;

export type SearchResultJiraBoardProjectContainer = {
	projectId: string;
	projectKey: string;
	projectName: string;
	projectTypeKey: SearchJiraProductType;
};

export type SearchResultJiraBoardUserContainer = {
	userId: string;
	userAccountId: string;
	userName: string;
};

export type SearchResultJiraBoardContainer =
	| SearchResultJiraBoardProjectContainer
	| SearchResultJiraBoardUserContainer;

export interface SearchResultJiraBoard extends SearchResult {
	boardId?: Maybe<string>;
	container?: Maybe<SearchResultJiraBoardContainer>;
	product: SearchJiraBoardProductType;
	__typename: 'SearchResultJiraBoard';
	board?: Maybe<{
		boardFrontendUrl: Maybe<string>;
		iconUrl: Maybe<string>;
	}>;
}

export const isSearchResultJiraBoard: (
	partial: SearchResult,
) => partial is SearchResultJiraBoard = (
	partial: SearchResult,
): partial is SearchResultJiraBoard => {
	return partial.__typename === 'SearchResultJiraBoard';
};

// TODO: Hydrate specific __typename for SearchResultJiraPlan
export interface SearchResultJiraPlan extends SearchResult {}

export const isSearchResultJiraPlan: (partial: SearchResult) => partial is SearchResultJiraPlan = (
	partial: SearchResult,
): partial is SearchResultJiraPlan => {
	return partial.__typename === 'SearchResultJiraPlan';
};

export type SearchJiraPartial =
	| SearchResultJiraProject
	| SearchResultJiraIssue
	| SearchResultJiraFilter
	| SearchResultJiraDashboard
	| SearchResultJiraBoard
	| SearchResultJiraPlan;

export const isSearchJiraPartial: (partial: SearchResult) => partial is SearchJiraPartial = (
	partial: SearchResult,
): partial is SearchJiraPartial => {
	return (
		isSearchResultJiraProject(partial) ||
		isSearchResultJiraIssue(partial) ||
		isSearchResultJiraFilter(partial) ||
		isSearchResultJiraDashboard(partial) ||
		isSearchResultJiraBoard(partial) ||
		isSearchResultJiraPlan(partial)
	);
};

export const getJiraPartials: () => string = () => {
	return `
		${getSearchResultJiraProject()}
		${SearchResultJiraIssue}
		${SearchResultJiraFilter}
		${SearchResultJiraDashboard}
		${SearchResultJiraBoard}
	`;
};

export {
	JiraStatusCategoryColor,
	type HydratedJiraIssue,
	type JiraHydrationData,
	type JiraStatusCategoryColorType,
} from './hydration/types';
export { fetchJiraFieldsQuery } from './hydration';
